import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { cn } from './cn'
import { Slot } from './Slot'
import { Spinner } from './Spinner'

import { buttonClasses, type ButtonSize, type ButtonVariant } from './button-styles'

export type { ButtonSize, ButtonVariant }

/** A price word: it holds a currency sign ("$49/month", "29 €/mois") or an amount per interval ("49 CHF/Monat"). */
const PRICE = /\p{Sc}|\d[^ ]*\/\p{L}/u

/**
 * cta, brand and outline labels are uppercase, but a price keeps its case: "JOIN $29/month", never
 * "JOIN $29/MONTH" (REDESIGN section 9, Q-LOOK-09). Words split on plain spaces only, so a formatted amount
 * with a no break space ("29 €") stays one word. One wrapper span keeps the words in a single flex item, so the
 * space between them renders as a space. Other children render untouched.
 */
function keepPriceCase(children: ReactNode): ReactNode {
  if (typeof children !== 'string' || !PRICE.test(children)) return children
  return (
    <span>
      {children.split(/( +)/).map((word, i) =>
        PRICE.test(word) ? (
          <span key={i} className="normal-case">
            {word}
          </span>
        ) : (
          word
        ),
      )}
    </span>
  )
}

const UPPERCASE: ReadonlySet<ButtonVariant> = new Set(['cta', 'brand', 'outline'])

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Shows a spinner, disables the button and keeps its width. */
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  fullWidth?: boolean
  /** Render the single child (for example a router Link) with button styles. */
  asChild?: boolean
  ref?: Ref<HTMLButtonElement>
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth,
  asChild,
  className,
  disabled,
  type,
  children,
  ref,
  ...rest
}: ButtonProps) {
  const classes = cn(buttonClasses({ variant, size, fullWidth }), loading && 'cursor-wait disabled:opacity-100', className)
  if (asChild) {
    return (
      <Slot className={classes} ref={ref as Ref<HTMLElement>} {...rest}>
        {children}
      </Slot>
    )
  }
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      {...rest}
    >
      <span className={cn('inline-flex items-center justify-center gap-2', loading && 'invisible')}>
        {leftIcon}
        {UPPERCASE.has(variant) ? keepPriceCase(children) : children}
        {rightIcon}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size={16} label="" />
        </span>
      )}
    </button>
  )
}
